
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
    Search,
    User,
    Phone,
    Mail,
    ChevronLeft,
    ChevronRight,
    Loader2,
    Briefcase,
    MapPin,
    Calendar,
    Eye,
    X,
    Building2,
    CreditCard,
    FileText,
    Download
} from 'lucide-react';

const DSAPartnerInquiriesPage = () => {
    const [inquiries, setInquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [pagination, setPagination] = useState({});
    const [selectedInquiry, setSelectedInquiry] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const fetchInquiries = async (pageNum = 1, search = searchTerm, start = startDate, end = endDate) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            let url = `/api/admin/dsa-partner-inquiries?page=${pageNum}&limit=10&search=${search}`;

            if (start) url += `&startDate=${start}`;
            if (end) url += `&endDate=${end}`;

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setInquiries(response.data.data);
                setPagination(response.data.pagination);
                setPage(pageNum);
            }
        } catch (error) {
            console.error('Error fetching DSA inquiries:', error);
            toast.error('Failed to load DSA inquiries');
        } finally {
            setLoading(false);
        }
    };

    const handleDateSearch = () => {
        fetchInquiries(1, searchTerm, startDate, endDate);
    };

    const handleClearDates = () => {
        setStartDate('');
        setEndDate('');
        fetchInquiries(1, searchTerm, '', '');
    };

    const handleExportToExcel = async () => {
        try {
            const token = localStorage.getItem('token');
            let url = `/api/admin/dsa-partner-inquiries/export?search=${searchTerm}`;

            if (startDate) url += `&startDate=${startDate}`;
            if (endDate) url += `&endDate=${endDate}`;

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            // Create download link
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `dsa_partner_inquiries_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            toast.success('Excel file downloaded successfully!');
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.error('Failed to export data');
        }
    };

    // Check if download button should be shown
    const showDownloadButton = searchTerm || startDate || endDate;

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchInquiries(1, searchTerm);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const handleViewDetails = (inquiry) => {
        setSelectedInquiry(inquiry);
        setIsModalOpen(true);
    };
  
    const handleStatusUpdate = async (id, status) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`/api/admin/dsa-partner-inquiries/${id}/status`, { status }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                toast.success(`Inquiry marked as ${status}`);
                fetchInquiries(page, searchTerm, startDate, endDate);
                setIsModalOpen(false);
            }
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error(error.response?.data?.message || `Failed to update status`);
        }
    };

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800">DSA Partner Enquiries</h1>
                        <p className="text-slate-500 mt-1">Review and manage DSA registration applications</p>
                    </div>

                    <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
                        {/* Global Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                            <input
                                type="text"
                                placeholder="Search code, name, mobile..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-64"
                            />
                        </div>

                        {/* Download Button - Only visible when filters are active */}
                        {showDownloadButton && (
                            <button
                                onClick={handleExportToExcel}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2 whitespace-nowrap"
                            >
                                <Download size={18} />
                                Download Excel
                            </button>
                        )}
                    </div>
                </div>

                {/* Date Range Filter */}
                <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                    <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
                        <div className="flex items-center gap-2">
                            <Calendar className="text-slate-400" size={18} />
                            <span className="text-sm font-medium text-slate-600">Date Range:</span>
                        </div>

                        <div className="flex flex-wrap gap-3 items-center">
                            <div className="flex flex-col">
                                <label className="text-xs text-slate-500 mb-1">Start Date</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                            </div>

                            <div className="flex flex-col">
                                <label className="text-xs text-slate-500 mb-1">End Date</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                            </div>

                            <div className="flex gap-2 mt-5">
                                <button
                                    onClick={handleDateSearch}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm flex items-center gap-2"
                                >
                                    <Search size={16} />
                                    Search
                                </button>

                                <button
                                    onClick={handleClearDates}
                                    className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors font-medium text-sm flex items-center gap-2"
                                >
                                    <X size={16} />
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-16">S.No</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">DSA Code</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Partner</th>
                                <th className="px-6 md:table-cell hidden py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                                <th className="px-6 md:table-cell hidden py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">City</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                Array(5).fill(0).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan="7" className="px-6 py-4 text-center">
                                            <div className="h-4 bg-slate-100 rounded w-full"></div>
                                        </td>
                                    </tr>
                                ))
                            ) : inquiries.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-slate-500">
                                        No DSA enquiries found
                                    </td>
                                </tr>
                            ) : (
                                inquiries.map((inquiry, index) => (
                                    <tr key={inquiry.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4 text-sm text-slate-500">
                                            {(pagination.page - 1) * 10 + index + 1}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                {inquiry.dsa_code || 'Pending'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                inquiry.status === 'Approved' ? 'bg-green-100 text-green-800' :
                                                inquiry.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                                                'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {inquiry.status || 'Pending'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                                                    <User size={16} />
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="font-medium text-slate-700">{inquiry.full_name}</span>
                                                    <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
                                                        <Phone size={12} />
                                                        {inquiry.mobile}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 md:table-cell hidden py-4">
                                            <div className="flex items-center gap-2 text-sm text-slate-600">
                                                <Briefcase size={14} className="text-slate-400" />
                                                {inquiry.business_type}
                                            </div>
                                        </td>
                                        <td className="px-6 md:table-cell hidden py-4 text-sm text-slate-600">
                                            {inquiry.city}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-600">
                                            {new Date(inquiry.created_at).toLocaleDateString('en-IN')}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                onClick={() => handleViewDetails(inquiry)}
                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                title="View Details"
                                            >
                                                <Eye size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!loading && pagination.totalPages > 1 && (
                    <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
                        <p className="text-sm text-slate-600">
                            Showing <span className="font-medium">{(page - 1) * 10 + 1}</span> to <span className="font-medium">{Math.min(page * 10, pagination.total)}</span> of <span className="font-medium">{pagination.total}</span> enquiries
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => fetchInquiries(page - 1)}
                                disabled={page === 1}
                                className="p-2 border border-slate-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronLeft size={20} />
                            </button>
                            <button
                                onClick={() => fetchInquiries(page + 1)}
                                disabled={page === pagination.totalPages}
                                className="p-2 border border-slate-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronRight size={20} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* View Details Modal */}
            {isModalOpen && selectedInquiry && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}></div>
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-200">
                                    <User size={24} />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-slate-800">{selectedInquiry.full_name}</h2>
                                    <p className="text-sm text-slate-500">DSA Code: <span className="font-semibold text-blue-600">{selectedInquiry.dsa_code}</span></p>
                                </div>
                            </div>
                            <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                                <X size={24} className="text-slate-400" />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto space-y-8">
                            {/* Grid Sections */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {/* Registration Info */}
                                <section className="space-y-4">
                                    <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                                        <FileText size={16} /> Registration Info
                                    </h3>
                                    <div className="bg-slate-50 p-4 rounded-xl space-y-3">
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-500">Mobile</span>
                                            <span className="font-medium text-slate-700">{selectedInquiry.mobile}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-500">Email</span>
                                            <span className="font-medium text-slate-700">{selectedInquiry.email}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-500">Assigned Branch</span>
                                            <span className="font-medium text-slate-700">{selectedInquiry.assigned_branch}</span>
                                        </div>
                                        <div className="flex flex-col gap-1 text-sm border-t border-slate-200 pt-3">
                                            <span className="text-slate-500">Address</span>
                                            <span className="font-medium text-slate-700 leading-relaxed">{selectedInquiry.address}, {selectedInquiry.city} - {selectedInquiry.pincode}</span>
                                        </div>
                                    </div>
                                </section>

                                {/* Basic Details */}
                                <section className="space-y-4">
                                    <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                                        <Building2 size={16} /> Business & Personal
                                    </h3>
                                    <div className="bg-slate-50 p-4 rounded-xl space-y-3">
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-500">GST Certification</span>
                                            <span className="font-medium text-slate-700">{selectedInquiry.business_constitution}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-500">PAN Name</span>
                                            <span className="font-medium text-slate-700">{selectedInquiry.full_name_as_pan}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-500">PAN No.</span>
                                            <span className="font-medium text-slate-700">{selectedInquiry.pan_number}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-500">Aadhaar No.</span>
                                            <span className="font-medium text-slate-700">{selectedInquiry.aadhaar_number}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-500">Date of Birth</span>
                                            <span className="font-medium text-slate-700">{new Date(selectedInquiry.dob).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                </section>

                                {/* Bank Details */}
                                <section className="space-y-4 md:col-span-2">
                                    <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                                        <CreditCard size={16} /> Banking Information
                                    </h3>
                                    <div className="bg-blue-50/50 p-4 rounded-xl grid grid-cols-1 md:grid-cols-3 gap-4 border border-blue-100">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs text-blue-600 font-medium">Account Number</span>
                                            <span className="font-bold text-slate-700">{selectedInquiry.account_number}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs text-blue-600 font-medium">IFSC Code</span>
                                            <span className="font-bold text-slate-700">{selectedInquiry.ifsc_code}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs text-blue-600 font-medium">Bank & Branch</span>
                                            <span className="font-medium text-slate-600">{selectedInquiry.bank_branch}</span>
                                        </div>
                                    </div>
                                </section>

                                {/* Documents */}
                                <section className="space-y-4 md:col-span-2">
                                    <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                                        <FileText size={16} /> Uploaded Documents
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {[
                                            { label: 'Profile Photo', name: selectedInquiry.profile_image_name, path: '/uploads/' },
                                            { label: 'ID Proof', name: selectedInquiry.doc_id_proof, path: '/api/documents/' },
                                            { label: 'Address Proof', name: selectedInquiry.doc_address_proof, path: '/api/documents/' },
                                            { label: 'PAN Card', name: selectedInquiry.doc_pan_card, path: '/api/documents/' },
                                            { label: 'Cancelled Cheque', name: selectedInquiry.doc_cancelled_cheque, path: '/api/documents/' },
                                            { label: 'GST Certificate', name: selectedInquiry.doc_photograph, path: '/api/documents/' },
                                        ].map((doc, i) => (
                                            <div key={i} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg group hover:border-blue-300 transition-colors">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-slate-100 rounded text-slate-500 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
                                                        <FileText size={16} />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-xs font-medium text-slate-400">{doc.label}</span>
                                                        <span className="text-sm text-slate-600 truncate max-w-[120px]">{doc.name || 'Not Uploaded'}</span>
                                                    </div>
                                                </div>
                                                {doc.name && (
                                                    <button 
                                                        onClick={() => {
                                                            if (!doc.name) return;

                                                            // If backend could NOT download the file (fallback),
                                                            // the name will end with "- Verified". Show info toast.
                                                            if (doc.name.trim().endsWith('- Verified')) {
                                                                toast.info("This document was verified via DigiLocker but the PDF could not be downloaded from Signzy. Please contact support.");
                                                                return;
                                                            }

                                                            // Otherwise open the file directly (works for both
                                                            // manual uploads AND DigiLocker PDFs saved to disk)
                                                            const basePath = doc.label === 'Profile Photo' ? '/api/uploads/' : '/api/documents/';
                                                            console.log(`Opening ${doc.label}: ${basePath}${doc.name}`);
                                                            window.open(`${basePath}${doc.name}`, '_blank');
                                                        }}
                                                        className="p-2 text-slate-400 hover:text-blue-600 transition-colors"
                                                        title="Download Document"
                                                    >
                                                        <Download size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            </div>
                        </div>

                         <div className="p-6 border-t border-slate-100 bg-slate-50/50 flex justify-between items-center">
                            <div className="flex gap-3">
                                {(!selectedInquiry.status || selectedInquiry.status === 'Pending') && (
                                    <>
                                        <button
                                            onClick={() => handleStatusUpdate(selectedInquiry.id, 'Approved')}
                                            className="px-4 py-2 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 transition-all shadow shadow-green-200"
                                        >
                                            Approve
                                        </button>
                                        <button
                                            onClick={() => handleStatusUpdate(selectedInquiry.id, 'Rejected')}
                                            className="px-4 py-2 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-all shadow shadow-red-200"
                                        >
                                            Reject
                                        </button>
                                    </>
                                )}
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-6 py-2 bg-slate-800 text-white font-medium rounded-xl hover:bg-slate-900 transition-all shadow-lg shadow-slate-200"
                            >
                                Close Details
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DSAPartnerInquiriesPage;
